import type {Awaitable} from '@vueuse/core';
import {mod} from '@/utils';

interface PaginatorChunk<T> {
	items: T[]
	total: number
	offset: number
}

export interface PaginatorResult<T> {
	value?: PaginatorChunk<T>
	done: boolean
}

export type PaginatorGenerator<T> = (params: {signal: AbortSignal, offset?: number}) => Awaitable<PaginatorResult<T> | null>;

// Nuxt 4 auto-imports `export default class X` under its declaration name, which Rolldown then
// cannot resolve, so classes in utils/ have to be named exports.
export class Paginator<T> {
	protected _initialTotal: number;

	static fromArray<Type>(array: Type[]) {
		const paginator = new Paginator<Type>((_signal, offset, pageSize) => Promise.resolve({
			items: array.slice(offset, offset + pageSize),
			total: array.length,
		}));

		paginator._initialTotal = array.length;

		return paginator;
	}

	static fromMappedArray<Type, MappedType>(
		array: Type[],
		mapper: (items: Type[], signal: AbortSignal) => Promise<MappedType[]>,
	) {
		const paginator = new Paginator<MappedType>(async (signal, offset, pageSize) => ({
			items: await mapper(array.slice(offset, offset + pageSize), signal),
			total: array.length,
		}));

		paginator._initialTotal = array.length;

		return paginator;
	}

	constructor(
		private callback: (
			signal: AbortSignal,
			offset: number,
			pageSize: number,
		) => Awaitable<Pick<PaginatorChunk<T>, 'items' | 'total'> | null>,
	) {
		this._initialTotal = Infinity;
	}

	linear(
		pageSize: number,
		initialOffset = 0,
		initialTotal?: number,
	): PaginatorGenerator<T> {
		if (initialOffset < 0) {
			throw new Error('Initial offset must be a non-negative number.');
		}

		let nextOffset = initialOffset;
		let total = initialTotal ?? this._initialTotal;

		return async ({signal, offset}: {signal: AbortSignal, offset?: number}) => {
			const currentOffset = offset ?? nextOffset;

			if (currentOffset >= total) {
				return {value: undefined, done: true};
			}

			const chunk = await this.callback(signal, currentOffset, pageSize);

			if (!chunk) {
				return null;
			}

			nextOffset = currentOffset + pageSize;
			total = chunk.total;

			return {
				value: {...chunk, offset: currentOffset},
				done: nextOffset >= total,
			};
		};
	}

	looping(
		pageSize: number,
		initialOffset = 0,
		initialTotal?: number,
	): PaginatorGenerator<T> {
		if (initialOffset < 0) {
			throw new Error('Initial offset must be a non-negative number.');
		}

		let nextOffset = initialOffset;
		let total = initialTotal ?? this._initialTotal;

		return async ({signal, offset}: {signal: AbortSignal, offset?: number}) => {
			let currentOffset = offset ?? nextOffset;

			if (currentOffset < 0 || currentOffset >= total) {
				if (!isFinite(total)) {
					throw new RangeError(`Invalid offset ${currentOffset}. Total count unknown.`);
				}

				const modulo = Math.ceil(total / pageSize) * pageSize;
				currentOffset = mod(currentOffset, modulo);
			}

			const chunk = await this.callback(signal, currentOffset, pageSize);

			if (!chunk) {
				return null;
			}

			total = chunk.total;
			nextOffset = currentOffset + pageSize;

			return {
				value: {...chunk, offset: currentOffset},
				done: false,
			};
		};
	}
}
